import { formatInTimeZone } from "date-fns-tz";

import {
  API_DATE_FORMAT,
  API_DATE_TIME_FORMAT,
  DEFAULT_EMAIL_OVERVIEW_RANGE_PRESET,
  EMAIL_OVERVIEW_RANGE_PRESETS,
  EMAIL_OVERVIEW_SORT_KEYS,
  EMAIL_TREND_METRICS,
  EMAIL_TREND_RANGE_PRESETS,
  EMAIL_TYPE_LABELS,
  END_OF_DAY,
  RATE_AXIS_INTEGER_SPAN,
  RATE_AXIS_STEPS,
} from "@/constants/email-analytics";
import type {
  EmailDateRange,
  EmailOverviewRangePreset,
  EmailOverviewSort,
  EmailRateAxis,
  EmailTrendGroupBy,
  EmailTrendMetric,
  EmailTrendPoint,
  EmailTrendRangePreset,
  TrendChartPoint,
  TrendHeadline,
  TrendMetricKind,
} from "@/types/email-analytics";

const formatApiDate = (date: Date) => {
  return formatInTimeZone(date, "UTC", API_DATE_FORMAT);
};

const getBrowserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const formatLocalApiDate = (date: Date) => {
  return formatInTimeZone(date, getBrowserTimeZone(), API_DATE_FORMAT);
};

const parseLocalApiDate = (date: string) => {
  const [year, month, day] = date.split("-").map(Number);

  return new Date(year, month - 1, day);
};

export const toApiDateTime = (date: string, edge: "start" | "end") => {
  const local = parseLocalApiDate(date);

  if (edge === "end") {
    local.setHours(END_OF_DAY.hours, END_OF_DAY.minutes, END_OF_DAY.seconds);
  }

  return formatInTimeZone(local, getBrowserTimeZone(), API_DATE_TIME_FORMAT);
};

const startOfTrendPeriod = (date: Date, groupBy: EmailTrendGroupBy) => {
  if (groupBy === "month") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
  }

  const daysSinceMonday = (date.getUTCDay() + 6) % 7;

  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - daysSinceMonday),
  );
};

const subtractMonths = (date: Date, months: number) => {
  const lastDayOfTargetMonth = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - months + 1, 0),
  ).getUTCDate();

  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth() - months,
      Math.min(date.getUTCDate(), lastDayOfTargetMonth),
    ),
  );
};

export const getTrendRange = (
  preset: EmailTrendRangePreset,
  groupBy: EmailTrendGroupBy = EMAIL_TREND_RANGE_PRESETS[preset].groupBy,
  now: Date = new Date(),
): EmailDateRange & { groupBy: EmailTrendGroupBy } => {
  const { months } = EMAIL_TREND_RANGE_PRESETS[preset];
  const monthsAgo = subtractMonths(now, months);

  return {
    from: formatApiDate(startOfTrendPeriod(monthsAgo, groupBy)),
    to: formatApiDate(now),
    groupBy,
  };
};

export const getLastDaysRange = (days: number, now: Date = new Date()): EmailDateRange => {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));

  return { from: formatLocalApiDate(from), to: formatLocalApiDate(now) };
};

export const getDaysRangeBefore = (range: EmailDateRange, days: number): EmailDateRange => {
  const start = parseLocalApiDate(range.from);
  const dayBefore = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1);

  return getLastDaysRange(days, dayBefore);
};

export const formatEmailType = (emailType: string | null) => {
  const key = emailType ?? "unknown";

  return EMAIL_TYPE_LABELS[key] ?? key.replaceAll("_", " ");
};

export const formatPercentage = (value: number | null, fractionDigits = 1) => {
  return value === null ? "—" : `${value.toFixed(fractionDigits)}%`;
};

export const formatCount = (value: number | null) => {
  return value === null ? "—" : new Intl.NumberFormat("en-US").format(value);
};

export const getTrendPeriodLabels = (periodStart: string, groupBy: EmailTrendGroupBy) => {
  const date = new Date(`${periodStart}T00:00:00Z`);

  return {
    label: formatInTimeZone(date, "UTC", groupBy === "month" ? "MMM yy" : "MMM d"),
    fullLabel: formatInTimeZone(
      date,
      "UTC",
      groupBy === "month" ? "MMMM yyyy" : "'Week of' MMM d, yyyy",
    ),
  };
};

const getTrendMetricParts = (point: EmailTrendPoint, metric: EmailTrendMetric) => {
  const bounced = point.hardBounces + point.softBounces;

  switch (metric) {
    case "openRate":
      return { value: point.openRate, numerator: point.uniqueOpens, denominator: point.delivered };
    case "clickRate":
      return {
        value: point.clickRate,
        numerator: point.uniqueClicks,
        denominator: point.delivered,
      };
    case "deliveryRate":
      return {
        value: point.deliveryRate,
        numerator: point.delivered,
        denominator: point.sent,
      };
    case "bounceRate":
      return { value: point.bounceRate, numerator: bounced, denominator: point.sent };
    case "spamReports":
      return { value: point.spamReports, numerator: null, denominator: null };
    case "sent":
      return { value: point.sent, numerator: null, denominator: null };
  }
};

export const buildTrendSeries = (
  points: EmailTrendPoint[],
  metric: EmailTrendMetric,
  groupBy: EmailTrendGroupBy,
): TrendChartPoint[] => {
  const isRate = EMAIL_TREND_METRICS[metric].kind === "rate";

  return points.map((point, index) => {
    const parts = getTrendMetricParts(point, metric);

    return {
      periodStart: point.periodStart,
      ...getTrendPeriodLabels(point.periodStart, groupBy),
      isCurrent: index === points.length - 1,
      hasData: isRate ? parts.value !== null : point.sent > 0,
      ...parts,
    };
  });
};

const findLastIndexWithData = (series: TrendChartPoint[]) => {
  return series.reduce((found, point, index) => {
    return point.hasData ? index : found;
  }, -1);
};

export const getTrendHeadline = (series: TrendChartPoint[]): TrendHeadline | null => {
  const completed = series.slice(0, -1);
  const latestIndex = findLastIndexWithData(completed);

  if (latestIndex === -1) {
    return null;
  }

  const previous = completed[latestIndex - 1];

  return {
    latest: completed[latestIndex],
    previous: previous?.hasData ? previous : null,
  };
};

export const getLastPointWithData = (series: TrendChartPoint[]) => {
  return series[findLastIndexWithData(series)];
};

export const hasPlottedCurrentValue = (series: TrendChartPoint[], kind: TrendMetricKind) => {
  const current = series.at(-1);

  if (!current) {
    return false;
  }

  return kind === "rate" ? current.value !== null : (current.value ?? 0) > 0;
};

export const buildRateAxis = (values: (number | null)[]): EmailRateAxis => {
  const present = values.filter((value) => {
    return value !== null;
  });

  if (present.length === 0) {
    return { domain: [0, 100], ticks: [0, 25, 50, 75, 100], fractionDigits: 0 };
  }

  const low = Math.min(99, Math.max(0, Math.floor(Math.min(...present) * 0.9)));
  const high = Math.max(low + 1, Math.min(100, Math.ceil(Math.max(...present) * 1.08)));
  const span = high - low;

  if (span < RATE_AXIS_INTEGER_SPAN) {
    const ticks = Array.from({ length: span * 2 + 1 }, (_, index) => {
      return low + index * 0.5;
    });

    return { domain: [low, high], ticks, fractionDigits: 1 };
  }

  const step = RATE_AXIS_STEPS.find((candidate) => {
    return candidate >= span / RATE_AXIS_INTEGER_SPAN;
  });
  const size = step ?? RATE_AXIS_STEPS[RATE_AXIS_STEPS.length - 1];
  const start = Math.floor(low / size) * size;
  const end = Math.min(100, Math.ceil(high / size) * size);
  const ticks = Array.from({ length: Math.floor((end - start) / size) + 1 }, (_, index) => {
    return start + index * size;
  });

  return { domain: [start, ticks[ticks.length - 1]], ticks, fractionDigits: 0 };
};

export const computeTrendChange = (
  latest: number | null,
  previous: number | null,
  kind: TrendMetricKind,
) => {
  if (latest === null || previous === null) {
    return null;
  }

  if (kind === "rate") {
    return latest - previous;
  }

  return previous === 0 ? null : ((latest - previous) / previous) * 100;
};

export const formatTrendValue = (value: number | null, kind: TrendMetricKind) => {
  if (value === null) {
    return "No data";
  }

  return kind === "rate" ? formatPercentage(value) : formatCount(value);
};

export const getTodayApiDate = (now: Date = new Date()) => {
  return formatLocalApiDate(now);
};

export const getOverviewPresetRange = (
  preset: EmailOverviewRangePreset,
  now: Date = new Date(),
): EmailDateRange => {
  switch (preset) {
    case "last30Days":
      return getLastDaysRange(EMAIL_OVERVIEW_RANGE_PRESETS.last30Days.days, now);
    case "last90Days":
      return getLastDaysRange(EMAIL_OVERVIEW_RANGE_PRESETS.last90Days.days, now);
    case "thisYear":
      return {
        from: formatLocalApiDate(new Date(now.getFullYear(), 0, 1)),
        to: formatLocalApiDate(now),
      };
  }
};

export const resolveOverviewRange = (
  from: string | undefined,
  to: string | undefined,
  now: Date = new Date(),
): EmailDateRange => {
  if (from && to && from <= to && to <= getTodayApiDate(now)) {
    return { from, to };
  }

  return getOverviewPresetRange(DEFAULT_EMAIL_OVERVIEW_RANGE_PRESET, now);
};

export const isOverviewSort = (value: unknown): value is EmailOverviewSort => {
  return (
    typeof value === "string" &&
    EMAIL_OVERVIEW_SORT_KEYS.some((key) => {
      return value === key || value === `-${key}`;
    })
  );
};

export const formatApiDateLabel = (date: string) => {
  return formatInTimeZone(new Date(`${date}T00:00:00Z`), "UTC", "MMM d, yyyy");
};

export const matchesEmailTypeSearch = (emailType: string, search: string) => {
  const query = search.trim().toLowerCase();

  return (
    formatEmailType(emailType).toLowerCase().includes(query) ||
    emailType.toLowerCase().includes(query)
  );
};
